import React, { useState, useEffect } from 'react';
import { InputTextColumnConfig, MultiSelectionColumnConfig, OptionsCustomColumnConfig, UsersSelectionColumnConfig } from '@/types/TableListEditable';
import TableListEditableMultiSelection from '../TableListEditableMultiSelection';
import InputText from '../../InputText/InputText';
import OptionsCustom from '../../OptionsCustom/OptionsCustom';
import UsersSelection from '../../UsersSelection/UsersSelection';
import styles from '../TableListEditableNew.module.scss';

type CellRendererParams = { value: unknown; data: Record<string, unknown> };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const applyChange = (nextValue: unknown, params: CellRendererParams, config: any, columnName: string) => {
  if (config?.onChange) {
    config.onChange({ rowData: params.data, columnName, value: nextValue });

    return;
  }

  params.data[columnName] = nextValue;
};

export const buildInputTextCellRenderer = (
  columnName: string,
  config?: InputTextColumnConfig,
) => {
  function InputTextCellRenderer(params: CellRendererParams) {
    const [localValue, setLocalValue] = useState(String(params.value ?? ''));

    useEffect(() => {
      setLocalValue(String(params.value ?? ''));
    }, [params.value]);

    if (!params.data) return null;

    return (
      <InputText
        id={`input-column-${columnName}`}
        value={localValue}
        size="small"
        handleChange={(ev) => {
          const newValue = ev.target.value;

          if (config?.onChange) {
            config.onChange({ rowData: params.data, columnName, value: newValue });
            setLocalValue(String(params.data[columnName] ?? ''));
          } else {
            params.data[columnName] = newValue;
            setLocalValue(newValue);
          }
        }}
        onBlur={(ev) => {
          const newValue = ev.target.value;

          if (config?.onBlur) {
            config.onBlur({ rowData: params.data, columnName, value: newValue });
          }
        }}
        formControlClass={styles.CellFormat}
        {...(config?.maxLength !== undefined && { maxLength: config.maxLength })}
      />
    );
  }

  return InputTextCellRenderer;
};

export const buildMultiSelectionCellRenderer = (
  columnName: string,
  config: MultiSelectionColumnConfig,
) => {
  function MultiSelectionCellRenderer(params: CellRendererParams) {
    if (!params.data) return null;

    return (
      <TableListEditableMultiSelection
        id={`multiselection-${columnName}-${String(params.data?.[columnName] ?? '')}`}
        options={config.options}
        value={params.value}
        getAllSelectedData={config.getAllSelectedData}
        getOptionValue={config.getOptionValue}
        multiSelectionProps={config.multiSelectionProps}
        onChange={(nextValue) => applyChange(nextValue, params, config, columnName)}
        formControlClass={styles.CellFormat}
      />
    );
  }

  return MultiSelectionCellRenderer;
};

export const buildUsersSelectionCellRenderer = (
  columnName: string,
  config?: UsersSelectionColumnConfig,
) => {
  function UsersSelectionCellRenderer(params: CellRendererParams) {
    if (!params.data) return null;

    return (
      <UsersSelection
        id={`userselection-${columnName}-${String(params.data?.id ?? params.data?.[columnName] ?? '')}`}
        value={params.value as string[]}
        displayInTable
        popupTitle={config?.popupTitle}
        buttonLabel={config?.buttonLabel}
        isOnlyOneUserSelection={config?.isOnlyOneUserSelection}
        onlyOneUserIsObject={config?.onlyOneUserIsObject}
        onChange={(nextValue) => applyChange(nextValue, params, config, columnName)}
      />
    );
  }

  return UsersSelectionCellRenderer;
};

export const buildOptionsCustomCellRenderer = (
  columnName: string,
  config: OptionsCustomColumnConfig,
) => {
  function OptionsCustomCellRenderer(params: CellRendererParams) {
    if (!params.data) return null;
    const currentValue = Array.isArray(params.value) ? params.value as string[] : [];

    return (
      <OptionsCustom
        key={currentValue.join(',')}
        id={`options-custom-${columnName}-${String(params.data?.id ?? '')}`}
        value={currentValue}
        displayInTable
        defaultOptions={(config.defaultOptions ?? [])}
        onChange={(nextValue) => applyChange(nextValue, params, config, columnName)}
        {...config.componentProps}
      />
    );
  }

  return OptionsCustomCellRenderer;
};
